/**
 * Creates a debounced version of a function that prevents execution
 * if called within the specified debounce duration.
 *
 * @param callback - The function to debounce
 * @param debounceMs - Debounce duration in milliseconds
 * @returns A debounced version of the callback
 */
export function createDebouncedHandler<T extends (...args: any[]) => void>(
  callback: T,
  debounceMs: number,
): T {
  let lastExecutionTime = 0

  return ((...args: Parameters<T>) => {
    const now = Date.now()
    if (now - lastExecutionTime < debounceMs) {
      return
    }
    lastExecutionTime = now
    callback(...args)
  }) as T
}

/**
 * Creates multiple debounced handlers that share the same debounce state.
 * This ensures that any handler execution resets the timer for all handlers.
 *
 * @param callbacks - Array of functions to debounce
 * @param debounceMs - Debounce duration in milliseconds
 * @returns Array of debounced versions of the callbacks
 */
export function createSharedDebouncedHandlers<
  T extends (...args: any[]) => void,
>(callbacks: T[], debounceMs: number): T[] {
  let lastExecutionTime = 0

  return callbacks.map((callback) => {
    return ((...args: Parameters<T>) => {
      const now = Date.now()
      if (now - lastExecutionTime < debounceMs) {
        return
      }
      lastExecutionTime = now
      callback(...args)
    }) as T
  })
}
